import StateObserver, { now, user } from 'src/StateObserver';
import type { DreamDriftIntroMode } from 'src/game/dreamDrift/popups/PopupDreamDriftIntro';
import type { DreamDriftHeroSelectionMode } from 'src/game/dreamDrift/popups/PopupDreamDriftHeroSelection';
import {
  type DreamDriftMapOpenOptions,
  dreamDriftMilestoneChestTypes,
} from 'src/game/dreamDrift/dreamDrift.types';
import {
  dreamDriftGetCareGate,
  dreamDriftHandlePet,
} from 'src/game/dreamDrift/dreamDrift.pet';
import { getGameScene } from 'src/game/minigame/match2-timestep/game/getGameScene';
import { assertNever } from 'src/replicant/utils';
import {
  closeAllPopups,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import { getApplication, waitForItPromise } from 'src/lib/utils';
import {
  dreamDriftCanSelectHero,
  dreamDriftCanStartInstance,
  dreamDriftGetCheckpoint,
  dreamDriftGetMilestones,
  dreamDriftGetPuzzleLevel,
  dreamDriftHasRewardsAvailable,
  dreamDriftHasCompletedCurrentEvent,
  dreamDriftHasStartedCurrentEvent,
  dreamDriftIsInstanceActive,
  dreamDriftShouldAutoOpenCurrentEvent,
  dreamDriftShouldAutoOpenFirstTime,
} from 'src/replicant/dreamDrift/dreamDrift.getters';
import {
  dreamDriftHeroes,
  dreamDriftUnlockLevel,
  type DreamDriftHero,
} from 'src/replicant/dreamDrift/dreamDrift.ruleset';
import { resolvePromoGroupFeature } from 'src/game/promo/promo.launch';
import { getPlayerProfile } from 'src/replicant/profile/profile.getters';
import { getCurrentPetState } from 'src/replicant/fate/fate.getters';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import { dreamDriftIsResurrectionLocked } from './dreamDrift.utils';

/**
 * Open the appropriate Dream Drift entry flow for the current player.
 *
 * @param origin Entry point used to attribute the locked-feature message.
 * @returns A promise that resolves when the entry flow closes.
 */
export async function dreamDriftOpen(
  origin: 'launch' | 'hud' = 'hud',
): Promise<void> {
  // Explain the OA preview's level gate before entering any Dream Drift flow.
  if (dreamDriftIsResurrectionLocked()) {
    await openPopupPromise('popupModalWhite', {
      title: i18n('dreamDrift.lockedTitle'),
      message: i18n('dreamDrift.lockedMessage', {
        level: dreamDriftUnlockLevel,
      }),
      origin,
      tracking: {
        feature: Feature.DREAM_DRIFT._,
        $subFeature: Feature.DREAM_DRIFT.BUTTON,
      },
    });
    return;
  }

  // Capture the current event state before choosing any entry presentation.
  const state = user();
  const currentTime = now();

  // Pending rewards always enter through the map that presents their chest.
  if (dreamDriftHasRewardsAvailable(state, currentTime)) {
    return dreamDriftOpenMap();
  }

  // Completed events reopen only their recap without running another entry flow.
  const hasCompletedCurrentEvent = dreamDriftHasCompletedCurrentEvent(
    state,
    currentTime,
  );
  if (hasCompletedCurrentEvent) {
    return openPopupPromise('popupDreamDriftComplete', {});
  }

  // Sleeping pets get feedback without consuming the FTUE; active runs remain accessible.
  if (
    !dreamDriftIsInstanceActive(state, currentTime) &&
    getCurrentPetState(state, currentTime) === 'asleep'
  ) {
    await dreamDriftHandlePet();
    return;
  }

  // Capture both lifetime and weekly promo state before recording this view.
  const shouldShowFtue = dreamDriftShouldAutoOpenFirstTime(state, currentTime);
  const shouldResolveCurrentEvent = dreamDriftShouldAutoOpenCurrentEvent(
    state,
    currentTime,
  );

  // Reconcile expired progress and retain existing Dream Drift seen state.
  await StateObserver.invoke.dreamDriftSeenAt();

  // Record an eligible event view before any popup can interrupt the flow.
  if (shouldResolveCurrentEvent) {
    await StateObserver.invoke.promoWasShown('dreamDriftEventPromo');
  }

  // Show the existing fullscreen Dream Drift instructions only once per account.
  if (shouldShowFtue) {
    await openPopupPromise('popupDreamDriftInfo', {});
  }

  // Satisfy this weekly promo when Dream Drift opened from any entry surface.
  if (shouldResolveCurrentEvent) {
    await resolvePromoGroupFeature({
      groupId: 'ftueFeatures',
      itemId: 'dreamDrift',
    });
  }

  // Continue into the refreshable current-event entry flow.
  await dreamDriftOpenCurrentState();
}

/**
 * Open Dream Drift for the player's current event and pet state.
 *
 * @returns A promise that resolves when the current entry flow closes.
 */
async function dreamDriftOpenCurrentState(): Promise<void> {
  // Read the entry lifecycle and pet state from the same snapshot.
  const state = user();
  const currentTime = now();

  // Ongoing instances ignore pet changes that would block a new entry.
  if (dreamDriftIsInstanceActive(state, currentTime)) {
    return dreamDriftOpenMap();
  }

  // Resolve pet gates before starting or reopening an inactive instance.
  const careGate = dreamDriftGetCareGate(state, currentTime);

  // Hard-blocked pets present their existing feedback instead of opening Dream Drift.
  if (careGate?.action === 'blocked') {
    await dreamDriftHandlePet();
    return;
  }

  // Only an instance from the active schedule window suppresses the intro.
  const hasStartedCurrentEvent = dreamDriftHasStartedCurrentEvent(
    state,
    currentTime,
  );

  // Available pets with an existing instance retain their current map destination.
  if (!careGate && hasStartedCurrentEvent) return dreamDriftOpenMap();

  // Select the intro action or present feedback for every care-gate outcome.
  const careAction = careGate?.action ?? 'available';
  let introMode: DreamDriftIntroMode;
  switch (careAction) {
    case 'revive':
      introMode = 'revive';
      break;

    case 'care':
      introMode = 'cure';
      break;

    case 'available':
      introMode = 'begin';
      break;

    default:
      return assertNever(careAction);
  }

  // Present the introduction with copy and CTA matching the current pet state.
  const action = await openPopupPromise('popupDreamDriftIntro', {
    mode: introMode,
  });
  if (!action) return;

  // Resolve the current pet state only after the player selects the CTA.
  const petHandlingResult = await dreamDriftHandlePet();

  // Continue, refresh, or stop for every pet-handling outcome.
  switch (petHandlingResult) {
    case 'blocked':
      return;

    case 'handled':
      return dreamDriftOpenCurrentState();

    case 'notNeeded':
      if (action !== 'begin') return dreamDriftOpenCurrentState();
      break;

    default:
      return assertNever(petHandlingResult);
  }

  // An available pet accepting BEGIN continues to the progression map.
  await dreamDriftOpenMap();
}

/**
 * Open the Dream Drift progression map popup.
 *
 * @returns A promise that resolves when the popup closes.
 */
export async function dreamDriftOpenMap(): Promise<void> {
  // Read one state snapshot before claiming any available rewards.
  const state = user();
  const currentTime = now();
  const instance = state.dreamDrift?.instance;
  const hasRewardsAvailable = dreamDriftHasRewardsAvailable(state, currentTime);

  // Prepare the popup arguments for this map opening.
  const mapOptions: DreamDriftMapOpenOptions = {};
  if (hasRewardsAvailable && instance) {
    // Resolve the chest tier from completion or the highest reached milestone.
    const maxLevelReached = Math.max(
      instance.maxLevelReached ?? 0,
      instance.progress,
    );
    const milestones = dreamDriftGetMilestones();
    let milestoneIndex: number;
    if (instance.result === 'completed') {
      milestoneIndex = milestones.length - 1;
    } else {
      const checkpoints = milestones.slice(0, -1);
      const reachedCheckpoints = checkpoints.map(
        ({ level }) => level <= maxLevelReached + 1,
      );
      milestoneIndex = reachedCheckpoints.lastIndexOf(true);
    }

    if (milestoneIndex >= 0) {
      mapOptions.rewardChestType =
        dreamDriftMilestoneChestTypes[milestoneIndex];
    }
  }

  // Open the registered Dream Drift map with any reward presentation attached.
  await openPopupPromise('popupDreamDriftMap', mapOptions);
}

/**
 * Open the Dream Drift hero-selection popup.
 *
 * @param mode Whether selection starts or continues an instance.
 * @param selectedHero Hero that should be selected when the popup opens.
 * @returns The selected Dream Drift hero.
 */
export async function dreamDriftSelectHero(
  mode: DreamDriftHeroSelectionMode,
  selectedHero?: DreamDriftHero,
): Promise<DreamDriftHero> {
  // Let the fullscreen popup preserve or initialize the active selection.
  return openPopupPromise('popupDreamDriftHeroSelection', {
    mode,
    selectedHero,
  });
}

/**
 * Select a hero, play the current Dream Drift puzzle, and reopen the map.
 *
 * @returns Whether a valid Dream Drift puzzle was played and completed.
 */
export async function dreamDriftPlayPuzzle(): Promise<boolean> {
  // Check whether the player can start or continue an instance.
  const state = user();
  const currentInstance = state.dreamDrift?.instance;
  const currentTime = now();
  const shouldStartInstance = dreamDriftCanStartInstance(state, currentTime);
  const canContinueInstance =
    currentInstance !== undefined &&
    dreamDriftCanSelectHero(state, currentTime);
  const canPlay = shouldStartInstance || canContinueInstance;
  if (!canPlay) return false;

  // Only a hero selected in this event may carry into hero selection.
  const hasStartedCurrentEvent = dreamDriftHasStartedCurrentEvent(
    state,
    currentTime,
  );
  let selectedHero = dreamDriftHeroes.find(
    (hero) => hasStartedCurrentEvent && hero === currentInstance?.hero,
  );
  let selectionMode: DreamDriftHeroSelectionMode = 'continue';
  if (shouldStartInstance) selectionMode = 'begin';

  const shouldSelectHero = shouldStartInstance || !selectedHero;
  if (shouldSelectHero) {
    selectedHero = await dreamDriftSelectHero(selectionMode, selectedHero);
  }
  if (!selectedHero) return false;

  // Start a new instance with the selected hero when needed.
  if (shouldStartInstance) {
    const startedAt = await StateObserver.invoke.dreamDriftStartInstance({
      hero: selectedHero,
    });
    if (startedAt === undefined) return false;
  }

  // Resolve the next puzzle from the active instance's seeded rotation.
  const activeState = user();
  let instance = activeState.dreamDrift?.instance;
  if (!instance) return false;

  const node = instance.progress + 1;
  const puzzleLevel = dreamDriftGetPuzzleLevel(activeState, node);
  if (puzzleLevel === undefined) return false;

  // Open pre-game with the selected hero and current puzzle goals.
  const confirmedHero = await openPopupPromise('popupDreamDriftPregame', {
    level: node,
    puzzleLevel,
    selectedHero,
    selectionMode,
  });
  if (!confirmedHero) return false;

  // Switch the instance hero when pre-game changed the selection.
  if (instance.hero !== confirmedHero) {
    const switched = await StateObserver.invoke.dreamDriftSwitchHero({
      startedAt: instance.startedAt,
      hero: confirmedHero,
    });
    if (!switched) return false;

    // Refresh the instance after the accepted hero switch.
    instance = user().dreamDrift?.instance;
    if (!instance) return false;
  }

  // Resolve the screen manager before starting backend puzzle state.
  const { screenManager } = getApplication();

  // Let the backend validate every condition required to start the confirmed node.
  const started = await StateObserver.invoke.dreamDriftStartLevel({
    startedAt: instance.startedAt,
    node,
  });
  if (!started) return false;

  let levelFinished = false;
  try {
    // Transition into the Dream Drift puzzle scene behind the open map.
    await screenManager.showScreen('minigame', {
      minigame: 'dream-drift',
      levelNumber: puzzleLevel,
      hero: instance.hero as DreamDriftHero,
    });

    // Start the preloaded puzzle track before revealing the board.
    sounds.music.play('dreamDriftPuzzle');

    // Dismiss the map only after the puzzle scene finishes transitioning.
    closeAllPopups();

    // Wait for the player to finish the active puzzle attempt.
    const game = getGameScene();
    if (!game) throw new Error('Dream Drift puzzle scene was not created');
    await game.waitForComplete();

    // Reconcile the active instance before applying its reported gameplay result.
    await StateObserver.invoke.dreamDriftSeenAt();

    // Expiry takes precedence over every gameplay result for the matching run.
    const instanceResult = user().dreamDrift?.instance?.result;
    if (instanceResult === 'expired') return false;

    // Broken levels roll back as quits instead of advancing progression.
    const result = game.getResult();
    const dreamDriftResult = result === 'broken' ? 'quit' : result;
    const finishResult = await StateObserver.invoke.dreamDriftFinishLevel({
      startedAt: instance.startedAt,
      result: dreamDriftResult,
    });
    if (!finishResult) throw new Error('Dream Drift level finish was rejected');
    levelFinished = true;

    // Celebrate every accepted win before the map and any completion rewards.
    if (result === 'win') {
      await openPopupPromise('popupMatch2LevelComplete', {
        dreamDrift: node,
        level: puzzleLevel,
        stars: game.getPuzzleResults().score?.stars,
        origin: 'postLevel',
      });
    }

    // Report broken levels as failures so the caller can display its own warning.
    return result !== 'broken';
  } catch {
    // Roll back technical failures without leaving an active Dream Drift node.
    if (!levelFinished) {
      await StateObserver.invoke.dreamDriftFinishLevel({
        startedAt: instance.startedAt,
        result: 'quit',
      });
    }
    return false;
  } finally {
    // Cover the puzzle with the map before transitioning to the pet screen below it.
    const mapPromise = dreamDriftOpenMap();

    // Let the map opening animation cover the puzzle before changing screens.
    await waitForItPromise(200);

    // Restore the pet screen without replacing the open map's music.
    await screenManager.showScreen('pet');

    // Keep the gameplay sequence active until the player closes the map.
    await mapPromise;
  }
}

/**
 * Offer the custom gem continue for the active Dream Drift puzzle.
 *
 * @param price Gem price of the current shared Match-2 continue tier.
 * @returns Whether the player accepted the continue, or false without an active node.
 */
export async function dreamDriftContinue(price: number): Promise<boolean> {
  // Read the active progression, hero, and player identity from one snapshot.
  const state = user();
  const instance = state.dreamDrift?.instance;

  // Only positive node values identify a puzzle that can be continued.
  const currentLevel = instance?.playing ?? 0;
  if (currentLevel <= 0) return false;

  // Resolve the checkpoint restored on defeat.
  const returnLevel = dreamDriftGetCheckpoint(state, currentLevel) || 1;

  // Pass only configured heroes through to the popup's pet presentation.
  const hero = dreamDriftHeroes.find(
    (configuredHero) => configuredHero === instance?.hero,
  );

  // Resolve the avatar through the shared public, private, and generic fallbacks.
  const profile = getPlayerProfile(
    state,
    state.publicProfile?.name,
    state.publicProfile?.photo,
    state.profile?.name,
    state.profile?.photo,
  );

  // Present the progression consequence and return the player's decision.
  return openPopupPromise('popupDreamDriftContinue', {
    currentLevel,
    returnLevel,
    price,
    avatar: profile.photo,
    hero,
  });
}
